import {
    ArrowTopRightIcon,
    CheckIcon,
    HelpCenterIcon,
    LogoutIcon,
    PolicyIcon,
    QuestionIcon,
    ScanQrCodeIcon,
    UserIcon,
} from "@xola/icons";
import { Sidebar } from "@xola/ui-kit";
import { orderBy } from "lodash";
import React, { useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { MobileAppsMessages, XolaMobileApp } from "types";
import { signOut } from "../../auth/signOut";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { SellerLogo } from "@/components/Common/SellerLogo";
import { FeedbackWidget } from "@/components/Feedback/FeedbackWidget";
import { useXolaMobileApps } from "@/components/MobileApps/XolaMobileAppsContext";
import { useAccount, useSeller } from "@/hooks/useAccount";
import { useSwitchDelegator } from "@/hooks/useSwitchDelegator";
import { getAddress } from "@/models/Seller";
import { useOpenAccountStore } from "@/stores/openAccountStore";

export const MenuSidebarFooter = React.memo(() => {
    const history = useHistory();
    const seller = useSeller();
    const { data } = useAccount();
    const { user = null, delegators = [] } = data ?? {};
    const { sendMessage, xolaMobileApp } = useXolaMobileApps();
    const footerMenuInstanceRef = useRef<{ hide: () => void } | null>(null);

    const handleLogoutClick = () => {
        if (xolaMobileApp === XolaMobileApp.XolaTablet) {
            sendMessage({ type: MobileAppsMessages.LOGOUT });
        }

        void signOut(); // Revokes where there is something to revoke, then reloads the whole app
    };

    const handleSupport = () => {
        history.push("/support");
    };

    const handleUserNameClick = () => {
        history.push("/settings/profile");
    };

    const handleScanQrClick = () => {
        sendMessage({ type: MobileAppsMessages.SCAN_QR_CODE });
    };

    return (
        <>
            {xolaMobileApp === XolaMobileApp.XolaTablet && (
                <Sidebar.Button
                    appearance="solid"
                    icon={ScanQrCodeIcon}
                    label="Scan QR"
                    className="mb-4"
                    onClick={handleScanQrClick}
                />
            )}
            <FeedbackWidget className="mb-2" />
            <Sidebar.Footer>
                <Sidebar.Menu
                    delay={200}
                    content={
                        <div className="space-y-3">
                            <div className="space-y-2">
                                <DelegatorsList onCloseFooterMenu={() => footerMenuInstanceRef.current?.hide()} />

                                <div>
                                    {delegators.length > 0 && <Sidebar.Separator className="my-0 mx-0 mt-4" />}
                                    <Sidebar.Button icon={PolicyIcon} label="Privacy Policy" onClick={handlePolicy} />
                                    <Sidebar.Button icon={QuestionIcon} label="Support" onClick={handleSupport} />
                                    <Sidebar.Button
                                        icon={HelpCenterIcon}
                                        label="Help Center"
                                        onClick={handleHelpCenter}
                                    />
                                    <Sidebar.Separator className="mx-0" />
                                    <Sidebar.Button
                                        className="no-translate"
                                        icon={UserIcon}
                                        label={user?.name}
                                        onClick={handleUserNameClick}
                                    />
                                    <Sidebar.Button icon={LogoutIcon} label="Logout" onClick={handleLogoutClick} />
                                </div>
                            </div>
                        </div>
                    }
                    onMount={(instance: { hide: () => void }) => {
                        footerMenuInstanceRef.current = instance;
                    }}
                >
                    <Sidebar.Account
                        name={seller.name}
                        image={<SellerLogo seller={seller} />}
                        className="no-translate !pr-6"
                    />
                </Sidebar.Menu>
            </Sidebar.Footer>
        </>
    );
});

const handleHelpCenter = () => {
    window.open("https://support.xola.com", "_blank")?.focus();
};

const handlePolicy = () => {
    window.open("https://www.xola.com/privacy-policy", "_blank")?.focus();
};

interface DelegatorsListProps {
    onCloseFooterMenu: () => void;
}

const DelegatorsList = ({ onCloseFooterMenu }: DelegatorsListProps) => {
    const seller = useSeller();
    const { data } = useAccount();
    const { switchDelegator } = useSwitchDelegator();
    const showOpenAccountModal = useOpenAccountStore((state) => state.show);
    const { sendMessage, xolaMobileApp } = useXolaMobileApps();

    const delegators = useMemo(() => data?.delegators ?? [], [data?.delegators]);
    const hasMoreAccounts = delegators.length > 1;

    const sortedDelegators = useMemo(() => {
        if (delegators.length === 0) return [];

        const others = orderBy(
            delegators.filter((d) => d.id !== seller.id),
            ["name"],
            ["asc"],
        );

        const active = delegators.find((d) => d.id === seller.id);
        return active ? [active, ...others] : others;
    }, [delegators, seller.id]);

    if (!data) {
        return null;
    }

    const handleMoreAccountsClick = () => {
        if (xolaMobileApp === XolaMobileApp.XolaTablet) {
            onCloseFooterMenu();
            sendMessage({ type: MobileAppsMessages.MORE_ACCOUNTS });
            return;
        }

        showOpenAccountModal();
    };

    return (
        <>
            <div className="max-h-60 overflow-y-auto">
                {sortedDelegators.map((delegator) => (
                    <Sidebar.Account
                        key={delegator.id}
                        name={delegator.name}
                        description={getAddress(delegator)}
                        image={<SellerLogo seller={delegator} />}
                        icon={seller.id === delegator.id ? <CheckIcon className="text-green" /> : null}
                        onClick={async () => switchDelegator(delegator)}
                    />
                ))}
            </div>

            {hasMoreAccounts ? (
                <div className="px-4 pt-2">
                    <SecondaryButton
                        size="small"
                        icon={<ArrowTopRightIcon className="mt-0.5" />}
                        className="border-gray-dark bg-transparent py-2 font-normal hover:bg-gray-darker"
                        onClick={handleMoreAccountsClick}
                    >
                        More Accounts
                    </SecondaryButton>
                </div>
            ) : null}
        </>
    );
};
